// Certified Materials — React Query layer (query keys, list/detail queries,
// filter-option queries, write mutations).

import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  bulkMapSellerCompanies,
  bulkUpdateOrders,
  confirmDocumentsReview,
  confirmEvidence,
  deleteDocument,
  deleteOrders,
  fetchAvailableOrders,
  fetchBrandsOptions,
  fetchBuyersOptions,
  fetchCustomTagsOptions,
  fetchDocumentsPendingReview,
  fetchOrderById,
  fetchOrderChangesSinceCheck,
  fetchOrderDocumentCheck,
  fetchOrderEvidenceItems,
  fetchOrdersList,
  fetchPlatformStandards,
  fetchProducts,
  fetchResponsiblePersonsOptions,
  fetchSeasonsOptions,
  fetchStandardsOptions,
  fetchSupplierAssigneesOptions,
  fetchUnmappedSellers,
  fetchUsersByCompany,
  fetchVendorNamesOptions,
  fetchVendorsOptions,
  importOrders,
  mapSellerCompany,
  rematchOrderDocuments,
  requestDocumentReupload,
  revokeFieldMatchValidation,
  sendBulkReminders,
  sendBulkRequests,
  updateDocument,
  updateOrder,
  updateOrderStatus,
  uploadDocument,
  uploadOrderDocument,
  validateFieldMatch,
} from '@/api/certified-materials';
import { cmmPagination } from '@/api/certified-materials/compat';
import type {
  IApiDocument,
  IApiOrder,
  IApiOrdersResponse,
  IAssignee,
  IBulkSellerMappingBody,
  IBulkUpdateOrdersBody,
  IConfirmDocumentsReviewBody,
  IEvidenceItem,
  IImportOrdersBody,
  ILinkedOrder,
  ISellerMappingInput,
  ISendRemindersBody,
  ISendRequestEntry,
  IUpdateDocumentBody,
  IUpdateOrderBody,
  IUploadDocBody,
  IUploadOrderDocBody,
} from '@/api/certified-materials/types';
import {
  type UsePaginatedSelectResult,
  usePaginatedSelect,
} from '@/hooks/usePaginatedSelect';
import type {
  IDocumentContentCheck,
  IFilterOption,
  IOrdersListParams,
} from '../-types';
import { transformFieldMatchesToDocumentCheck } from '../-utils/orderDetails';

export const certifiedMaterialsKeys = {
  all: ['certified-materials'] as const,
  list: (params: IOrdersListParams) =>
    ['certified-materials', 'orders', 'list', params] as const,
  detail: (orderId: string) =>
    ['certified-materials', 'orders', 'detail', orderId] as const,
  document: (documentId: string) =>
    ['certified-materials', 'documents', documentId] as const,
  documentCheck: (orderId: string) =>
    ['certified-materials', 'orders', 'document-check', orderId] as const,
  changesSinceCheck: (orderId: string) =>
    ['certified-materials', 'orders', 'changes-since-check', orderId] as const,
  evidenceItems: (orderId: string) =>
    ['certified-materials', 'orders', 'evidence-items', orderId] as const,
  assignees: (companyId: string) =>
    ['certified-materials', 'assignees', companyId] as const,
  filterOptions: (kind: string) =>
    ['certified-materials', 'filter-options', kind] as const,
  unmappedSellers: () => ['certified-materials', 'unmapped-sellers'] as const,
  importJobs: (ids: string) =>
    ['certified-materials', 'import-jobs', ids] as const,
};

/** Build the query string for the orders-list endpoint. Lives in the hook layer
 * so the api layer stays pure (Rule 3). */
// Backend `orderFilterParamsSchema` types these filter fields as scalars
// (z.string / z.enum), NOT `string | array` like the others. The table stores
// every filter value as a string[], so these must be unwrapped to a single
// value — otherwise the list 400s ("Invalid filter parameter: must be valid
// JSON matching the OrderFilterParams schema").
const SCALAR_FILTER_KEYS = new Set(['orderNumber', 'aiStatus', 'documentId']);

function buildOrdersQs(params: IOrdersListParams): string {
  const sp = new URLSearchParams();
  sp.set('page', String(params.page));
  sp.set('limit', String(params.limit));
  sp.set('sortBy', params.sortBy);
  sp.set('sortOrder', params.sortOrder);
  if (params.quickFilter && params.quickFilter !== 'all')
    sp.set('quickFilter', params.quickFilter);
  // The DataTable `date` filter emits `orderDate: [from, to]`; the backend
  // expects flat `orderDateAfter`/`orderDateBefore` keys, so expand and drop
  // the synthetic `orderDate` key.
  const { orderDate, ...columnFilters } = params.filter ?? {};
  const [orderDateAfter, orderDateBefore] = Array.isArray(orderDate)
    ? (orderDate as string[])
    : [];
  // The "show only unmapped" banner toggle rides inside the filter JSON as a
  // boolean (backend: `seller_te_id IS NULL`), alongside any column filters.
  const rawFilter: Record<string, unknown> = {
    ...columnFilters,
    ...(orderDateAfter ? { orderDateAfter } : {}),
    ...(orderDateBefore ? { orderDateBefore } : {}),
    ...(params.unmapped ? { unmapped: true } : {}),
  };
  const filter: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(rawFilter)) {
    if (SCALAR_FILTER_KEYS.has(key) && Array.isArray(value)) {
      if (value.length > 0) filter[key] = value[0];
    } else {
      filter[key] = value;
    }
  }
  sp.set('filter', JSON.stringify(filter));
  return `?${sp.toString()}`;
}

// Shared between the hook and the route loader so prefetch + render hit the
// same cache entry.
export function ordersQueryOptions(params: IOrdersListParams) {
  return queryOptions({
    queryKey: certifiedMaterialsKeys.list(params),
    queryFn: () => fetchOrdersList(buildOrdersQs(params)),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useOrdersQuery(params: IOrdersListParams) {
  return useQuery(ordersQueryOptions(params));
}

export function useOrderDetailQuery(orderId: string | null) {
  return useQuery({
    queryKey: certifiedMaterialsKeys.detail(orderId ?? ''),
    queryFn: () => fetchOrderById(orderId!),
    enabled: !!orderId,
    staleTime: 15_000,
  });
}

const OPTION_FETCHERS = {
  standards: fetchStandardsOptions,
  // `vendors` is the legacy slug returning SELLER names (it backs the Seller
  // Name filter); `vendorNames` is the actual Vendor column's dropdown.
  vendors: fetchVendorsOptions,
  vendorNames: fetchVendorNamesOptions,
  buyers: fetchBuyersOptions,
  seasons: fetchSeasonsOptions,
  customTags: fetchCustomTagsOptions,
  supplierAssignees: fetchSupplierAssigneesOptions,
  brands: fetchBrandsOptions,
  responsiblePersons: fetchResponsiblePersonsOptions,
} as const;

// Option sets whose filter value is the item's `id` rather than its `name`.
// Buyers filter on company id — names are display-only and can collide.
const ID_KEYED_OPTIONS = new Set<string>(['supplierAssignees', 'buyers']);

type OptionKind = keyof typeof OPTION_FETCHERS;

export function useFilterOptionsQuery(kind: OptionKind) {
  return useQuery({
    queryKey: certifiedMaterialsKeys.filterOptions(kind),
    queryFn: async (): Promise<IFilterOption[]> => {
      const res = await OPTION_FETCHERS[kind]();
      // API items are { id, name, color? }. Assignees and buyers filter by id;
      // everything else filters by name (matches the old frontend).
      const useId = ID_KEYED_OPTIONS.has(kind);
      return (res.data ?? [])
        .filter(o => !!o?.name)
        .map(o => ({ key: useId ? o.id : o.name, label: o.name }));
    },
    staleTime: 5 * 60_000,
  });
}

// Products dropdown for the article-number filter. Keyed by `articleNumber`
// (not id) so the selected value matches how the backend filters `articleNumber`
// — same mapping as the old frontend. De-duped + sorted for the dropdown.
export function useProductsFilterQuery() {
  return useQuery({
    queryKey: certifiedMaterialsKeys.filterOptions('products'),
    queryFn: async (): Promise<IFilterOption[]> => {
      const res = await fetchProducts();
      const seen = new Set<string>();
      const out: IFilterOption[] = [];
      for (const p of res.data ?? []) {
        const articleNumber = p.articleNumber;
        if (!articleNumber || seen.has(articleNumber)) continue;
        seen.add(articleNumber);
        out.push({ key: articleNumber, label: articleNumber });
      }
      return out.sort((a, b) => a.label.localeCompare(b.label));
    },
    staleTime: 5 * 60_000,
  });
}

const LIST_KEY = ['certified-materials', 'orders', 'list'] as const;

// Optimistically merge `patch` into the cached detail + every cached list page
// for this order. Returns a rollback snapshot.
function applyOptimisticOrderPatch(
  qc: ReturnType<typeof useQueryClient>,
  orderId: string,
  patch: Partial<IApiOrder>,
) {
  const detailKey = certifiedMaterialsKeys.detail(orderId);
  const prevDetail = qc.getQueryData<IApiOrder>(detailKey);
  qc.setQueryData<IApiOrder>(detailKey, old =>
    old ? { ...old, ...patch } : old,
  );
  const prevLists = qc.getQueriesData<IApiOrdersResponse>({
    queryKey: LIST_KEY,
  });
  qc.setQueriesData<IApiOrdersResponse>({ queryKey: LIST_KEY }, old =>
    old
      ? {
          ...old,
          data: old.data.map(o => (o.id === orderId ? { ...o, ...patch } : o)),
        }
      : old,
  );
  return { prevDetail, prevLists, detailKey };
}

type OptimisticCtx = ReturnType<typeof applyOptimisticOrderPatch>;

function rollbackOptimistic(
  qc: ReturnType<typeof useQueryClient>,
  ctx: OptimisticCtx | undefined,
) {
  if (!ctx) return;
  if (ctx.prevDetail) qc.setQueryData(ctx.detailKey, ctx.prevDetail);
  for (const [key, data] of ctx.prevLists) qc.setQueryData(key, data);
}

// Platform standards for the order-line standards editor (value = shortName so
// it matches the strings stored on order lines).
export function useStandardsListQuery() {
  return useQuery({
    queryKey: ['certified-materials', 'platform-standards'] as const,
    queryFn: async (): Promise<IFilterOption[]> => {
      const res = await fetchPlatformStandards();
      const list = res?.data ?? [];
      const seen = new Set<string>();
      const out: IFilterOption[] = [];
      for (const s of list) {
        const label = s.shortName || s.name;
        if (!label || seen.has(label)) continue;
        seen.add(label);
        out.push({ key: label, label });
      }
      return out;
    },
    staleTime: 5 * 60_000,
  });
}

// Free-text custom tags have no server-side tag entity — "creating" one just
// appends it to the cached options so it shows up selected immediately; the
// value persists when the order is saved.
export function useCreateCustomTag() {
  const qc = useQueryClient();
  // Stable identity: it feeds the memoised column defs in the table.
  return useCallback(
    async (title: string) => {
      const key = certifiedMaterialsKeys.filterOptions('customTags');
      qc.setQueryData<IFilterOption[]>(key, old => {
        const list = old ?? [];
        return list.some(o => o.key === title)
          ? list
          : [...list, { key: title, label: title }];
      });
      return { value: title, label: title };
    },
    [qc],
  );
}

export function useUpdateOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orderId,
      body,
    }: {
      orderId: string;
      body: IUpdateOrderBody;
    }) => {
      // Apply optimistic patch before the request so the UI updates instantly.
      await qc.cancelQueries({
        queryKey: certifiedMaterialsKeys.detail(orderId),
      });
      await qc.cancelQueries({ queryKey: LIST_KEY });
      const ctx = applyOptimisticOrderPatch(
        qc,
        orderId,
        body as Partial<IApiOrder>,
      );
      try {
        const saved = await updateOrder(orderId, body);
        // Reconcile from the PATCH response, which echoes the saved order.
        //
        // The optimistic patch above merges the REQUEST BODY into the cached
        // order, which only lands correctly while a field is written under the
        // same name it is read. `supplierAssigneeIds` is not: it goes out as a
        // list of user ids and comes back as `supplierAssignees` objects. With
        // the body alone the chips would keep showing the previous assignees —
        // and because the invalidations below are `refetchType: 'none'`, no
        // refetch would correct it.
        if (saved?.id) {
          qc.setQueryData<IApiOrder>(
            certifiedMaterialsKeys.detail(orderId),
            old => (old ? { ...old, ...saved } : saved),
          );
          qc.setQueriesData<IApiOrdersResponse>({ queryKey: LIST_KEY }, old =>
            old
              ? {
                  ...old,
                  data: old.data.map(o =>
                    o.id === orderId ? { ...o, ...saved } : o,
                  ),
                }
              : old,
          );
        }
        // Mark stale only — the cache is already correct.
        void qc.invalidateQueries({
          queryKey: certifiedMaterialsKeys.all,
          refetchType: 'none',
        });
        void qc.invalidateQueries({
          queryKey: certifiedMaterialsKeys.detail(orderId),
          refetchType: 'none',
        });
      } catch (err) {
        rollbackOptimistic(qc, ctx);
        throw err;
      }
    },
  });
}

export function useUpdateOrderStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      orderId,
      status,
    }: {
      orderId: string;
      status: string;
    }) => {
      await qc.cancelQueries({
        queryKey: certifiedMaterialsKeys.detail(orderId),
      });
      await qc.cancelQueries({ queryKey: LIST_KEY });
      const ctx = applyOptimisticOrderPatch(qc, orderId, {
        status: status as IApiOrder['status'],
      });
      try {
        await updateOrderStatus(orderId, status);
        // Mark stale only — optimistic cache is already correct.
        void qc.invalidateQueries({
          queryKey: certifiedMaterialsKeys.all,
          refetchType: 'none',
        });
        void qc.invalidateQueries({
          queryKey: certifiedMaterialsKeys.detail(orderId),
          refetchType: 'none',
        });
      } catch (err) {
        rollbackOptimistic(qc, ctx);
        throw err;
      }
    },
  });
}

export function useImportOrders() {
  return useMutation({
    mutationFn: (body: IImportOrdersBody) => importOrders(body),
  });
}

// Evidence-document upload (POST /documents) — separate from the drawer's
// per-order upload (useUploadOrderDocument).
export function useUploadDocument() {
  return useMutation({
    mutationFn: (body: IUploadDocBody) => uploadDocument(body),
  });
}

const PENDING_REVIEW_QS = `?filter=${encodeURIComponent(JSON.stringify({ reviewStatus: 'pending_review' }))}`;

// Count of documents awaiting review — gates the Review Docs button. Polls so
// the count updates after AI processing finishes for a fresh upload.
export function usePendingReviewCountQuery() {
  return useQuery({
    queryKey: ['certified-materials', 'pending-review-count'] as const,
    queryFn: async (): Promise<number> => {
      const res = await fetchDocumentsPendingReview(PENDING_REVIEW_QS);
      return res.data?.length ?? 0;
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

// Full pending-review document list — fetched only while the review dialog is
// open. The dialog seeds editable local state from this.
export function usePendingReviewDocsQuery(enabled: boolean) {
  return useQuery({
    queryKey: ['certified-materials', 'pending-review-docs'] as const,
    queryFn: async (): Promise<IApiDocument[]> => {
      const res = await fetchDocumentsPendingReview(PENDING_REVIEW_QS);
      return res.data ?? [];
    },
    enabled,
    staleTime: 0,
  });
}

function toLinkableOrder(order: IApiOrder): ILinkedOrder {
  const lines = order.orderLines ?? [];
  // Standards repeat across lines on the same PO; the picker shows which
  // standards the order covers, not how often each appears.
  const standards = [...new Set(lines.flatMap(l => l.standards ?? []))];
  return {
    id: order.id,
    orderNumber: order.orderNumber,
    articleNumber: lines[0]?.articleNumber ?? '',
    articleName: lines[0]?.articleName ?? null,
    sellerName: order.sellerName,
    season: order.season,
    standards,
    lineCount: lines.length,
  };
}

// Stable identity: `usePaginatedSelect` maps items through this inside a memo.
const linkableOrderOption = (order: ILinkedOrder) => ({
  value: order.id,
  label: order.articleNumber
    ? `${order.orderNumber} - ${order.articleNumber}`
    : order.orderNumber,
});

/**
 * Orders available to link in the review dialog — server-searched and paged.
 *
 * This used to be one eager `/orders?limit=100`, which made the picker a
 * dead end past the first 100 orders: the dropdown's search box only filtered
 * those 100 rows in the browser, and there was no way to reach page two. The
 * endpoint is also heavy (fan-out per row), so pages stay small and nothing is
 * fetched until the dialog is open.
 */
export function useLinkableOrdersSelect(
  enabled: boolean,
): UsePaginatedSelectResult<ILinkedOrder> {
  return usePaginatedSelect<ILinkedOrder>({
    queryKey: ['certified-materials', 'linkable-orders'],
    queryFn: async ({ page, count, search }) => {
      const sp = new URLSearchParams();
      // `usePaginatedSelect` pages are 0-based; `/orders` is 1-based.
      sp.set('page', String(page + 1));
      sp.set('limit', String(count));
      // Due-date order (the endpoint default) reads as arbitrary in a picker;
      // by order number a paged list is scannable.
      sp.set('sortBy', 'orderNumber');
      sp.set('sortOrder', 'asc');
      // `search` only takes effect inside the JSON `filter` param — the CMM
      // `/orders` query schema has no top-level `search`, so a `?search=` is
      // parsed away and the endpoint quietly answers with the unfiltered
      // first page.
      if (search) sp.set('filter', JSON.stringify({ search }));
      const res = await fetchAvailableOrders(`?${sp.toString()}`);
      return {
        data: (res.data ?? []).map(toLinkableOrder),
        meta: { totalCount: cmmPagination(res)?.total ?? 0 },
      };
    },
    getOption: linkableOrderOption,
    enabled,
    pageSize: 50,
    staleTime: 60_000,
  });
}

export function useConfirmDocumentsReview() {
  return useMutation({
    mutationFn: (body: IConfirmDocumentsReviewBody) =>
      confirmDocumentsReview(body),
  });
}

// Update linked doc extracted data (PATCH /documents/:id) — triggered from
// DocumentDetailsSheet when the user edits and saves extracted fields.
export function useUpdateLinkedDocument() {
  return useMutation({
    mutationFn: ({
      documentId,
      body,
    }: {
      documentId: string;
      body: IUpdateDocumentBody;
    }) => updateDocument(documentId, body),
  });
}

// Plain document delete used by the review dialog (the drawer's
// useDeleteDocument is order-scoped and invalidates drawer queries).
export function useDeleteReviewDocument() {
  return useMutation({
    mutationFn: (documentId: string) => deleteDocument(documentId),
  });
}

// Bulk: apply one field change (status / assignee / due date / custom tags) to
// every selected order in a single request. Unlike useSubmitOrdersForReview
// (which loops per-order status PATCHes), this hits the batch /orders/bulk
// endpoint and invalidates the list cache ONCE on success.
export function useBulkUpdateOrders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, patch }: IBulkUpdateOrdersBody) => {
      const result = await bulkUpdateOrders(ids, patch);
      void qc.invalidateQueries({ queryKey: certifiedMaterialsKeys.all });
      return result;
    },
  });
}

export function useDeleteOrders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      await deleteOrders(ids);
      void qc.invalidateQueries({ queryKey: certifiedMaterialsKeys.all });
    },
  });
}

// ── Seller → network-company mapping ─────────────────────────────────────────

// The buyer's distinct unmapped sellers (+ counts) — powers the banner and the
// bulk-map dialog. Shares one key so a single/bulk map refreshes both.
export function useUnmappedSellersQuery(enabled = true) {
  return useQuery({
    queryKey: certifiedMaterialsKeys.unmappedSellers(),
    queryFn: fetchUnmappedSellers,
    enabled,
    staleTime: 30_000,
  });
}

// Both mapping mutations invalidate the orders list (so the "Map now" button
// and unmapped rows disappear) and the unmapped-sellers banner.
function invalidateAfterMapping(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: certifiedMaterialsKeys.all });
  void qc.invalidateQueries({
    queryKey: certifiedMaterialsKeys.unmappedSellers(),
  });
}

export function useMapSellerCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ISellerMappingInput) => {
      const result = await mapSellerCompany(body);
      invalidateAfterMapping(qc);
      return result;
    },
  });
}

export function useBulkMapSellers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: IBulkSellerMappingBody) => {
      const result = await bulkMapSellerCompanies(body);
      invalidateAfterMapping(qc);
      return result;
    },
  });
}

// Bulk: send document-submission requests to suppliers (brand).
export function useSendBulkRequests() {
  return useMutation({
    mutationFn: (entries: ISendRequestEntry[]) => sendBulkRequests(entries),
  });
}

// Bulk: remind suppliers about pending requests (brand).
export function useSendBulkReminders() {
  return useMutation({
    mutationFn: (body: ISendRemindersBody) => sendBulkReminders(body),
  });
}

// Bulk: submit selected orders for review (supplier). Status updates run
// sequentially — the cert-materials service has no batch status endpoint.
export function useSubmitOrdersForReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderIds: string[]) => {
      for (const orderId of orderIds) {
        await updateOrderStatus(orderId, 'in_review');
      }
      void qc.invalidateQueries({ queryKey: certifiedMaterialsKeys.all });
      return orderIds;
    },
  });
}

// ── Order-details drawer ─────────────────────────────────────────────────────

export function useOrderDocumentCheckQuery(
  orderId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: certifiedMaterialsKeys.documentCheck(orderId ?? ''),
    queryFn: async () => {
      const matches = await fetchOrderDocumentCheck(orderId!);
      return transformFieldMatchesToDocumentCheck(matches);
    },
    enabled: !!orderId && enabled,
    staleTime: 15_000,
  });
}

// What changed since the content check ran — the drawer's full comparison.
// Fetched for every checked order; an order never checked, and every order
// while the frontend still talks to CMM (which sends no `contentCheck`), costs
// no request.
export function useOrderChangesSinceCheckQuery(
  orderId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: certifiedMaterialsKeys.changesSinceCheck(orderId ?? ''),
    queryFn: async () => (await fetchOrderChangesSinceCheck(orderId!)).data,
    enabled: !!orderId && enabled,
    staleTime: 15_000,
  });
}

// Re-matching rewrites the `document_field_matches` rows that the Document
// Content Check, the Evidence section's status icons and the order-list AI tick
// all read, so every cert-materials query is stale afterwards — not just the
// document-check one the refresh button lives in.
export function useRematchOrderDocuments() {
  const invalidate = useDetailInvalidator();
  return useMutation({
    mutationFn: async (orderId: string) => {
      const result = await rematchOrderDocuments(orderId);
      invalidate(orderId);
      return result;
    },
  });
}

export function useOrderEvidenceItemsQuery(orderId: string | null) {
  return useQuery({
    queryKey: certifiedMaterialsKeys.evidenceItems(orderId ?? ''),
    queryFn: () => fetchOrderEvidenceItems(orderId!),
    enabled: !!orderId,
    staleTime: 15_000,
  });
}

export function useAssigneesQuery(companyId: string | null) {
  return useQuery({
    queryKey: certifiedMaterialsKeys.assignees(companyId ?? ''),
    queryFn: async (): Promise<IAssignee[]> => {
      const res = await fetchUsersByCompany(companyId!);
      const list = Array.isArray(res) ? res : (res.data ?? []);
      return list.map(u => ({
        id: u.id,
        name: u.name || u.email,
        email: u.email,
      }));
    },
    enabled: !!companyId,
    staleTime: 5 * 60_000,
  });
}

function useDetailInvalidator() {
  const qc = useQueryClient();
  return (orderId: string) => {
    void qc.invalidateQueries({
      queryKey: certifiedMaterialsKeys.all,
      refetchType: 'none',
    });
    void qc.invalidateQueries({
      queryKey: certifiedMaterialsKeys.detail(orderId),
      refetchType: 'none',
    });
  };
}

export function useConfirmEvidence(orderId: string) {
  const invalidate = useDetailInvalidator();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      evidenceId,
      confirmed,
    }: {
      evidenceId: string;
      confirmed: boolean;
    }) => {
      // Optimistic update
      const key = certifiedMaterialsKeys.evidenceItems(orderId);
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<IEvidenceItem[]>(key);
      const itemId = evidenceId.split(':').slice(0, -1).join(':');
      qc.setQueryData<IEvidenceItem[]>(key, old =>
        old?.map(item => {
          if (item.id !== itemId) return item;
          const requiredEvidence = item.requiredEvidence.map(e =>
            e.id === evidenceId ? { ...e, isCompleted: confirmed } : e,
          );
          return {
            ...item,
            requiredEvidence,
            completedCount: requiredEvidence.filter(e => e.isCompleted).length,
          };
        }),
      );
      try {
        await confirmEvidence(orderId, evidenceId, confirmed);
        void qc.invalidateQueries({
          queryKey: certifiedMaterialsKeys.evidenceItems(orderId),
          refetchType: 'none',
        });
        invalidate(orderId);
      } catch (err) {
        // Rollback optimistic update
        if (prev) qc.setQueryData(key, prev);
        throw err;
      }
    },
  });
}

export function useUploadOrderDocument(orderId: string) {
  const invalidate = useDetailInvalidator();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: IUploadOrderDocBody) => {
      await uploadOrderDocument(orderId, body);
      void qc.invalidateQueries({
        queryKey: certifiedMaterialsKeys.evidenceItems(orderId),
      });
      invalidate(orderId);
    },
  });
}

export function useDeleteDocument(orderId: string) {
  const invalidate = useDetailInvalidator();
  return useMutation({
    mutationFn: async (documentId: string) => {
      await deleteDocument(documentId);
      invalidate(orderId);
    },
  });
}

export function useRequestReupload() {
  return useMutation({
    mutationFn: ({
      documentId,
      message,
    }: {
      documentId: string;
      message: string;
    }) => requestDocumentReupload(documentId, message),
  });
}

export function useFieldMatchValidation(orderId: string) {
  const qc = useQueryClient();
  const key = certifiedMaterialsKeys.documentCheck(orderId);

  // Flip the field's manual-validation flag in place so the checkmark updates
  // instantly instead of waiting on the (slow) refetch (CMMM-304).
  const patchValidated = (
    matchId: string,
    fieldName: string,
    validated: boolean,
  ) =>
    qc.setQueryData<IDocumentContentCheck>(key, old =>
      old
        ? {
            ...old,
            documents: old.documents.map(doc =>
              doc.id !== matchId
                ? doc
                : {
                    ...doc,
                    verificationFields: doc.verificationFields.map(f =>
                      (f.fieldKey ?? f.name) === fieldName
                        ? { ...f, isManuallyValidated: validated }
                        : f,
                    ),
                  },
            ),
          }
        : old,
    );

  const run =
    (
      action: (matchId: string, fieldName: string) => Promise<unknown>,
      validated: boolean,
    ) =>
    async ({ matchId, fieldName }: { matchId: string; fieldName: string }) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<IDocumentContentCheck>(key);
      patchValidated(matchId, fieldName, validated);
      try {
        await action(matchId, fieldName);
        // Reconcile in the background — the optimistic patch already reflects
        // the change, so mark stale without a blocking refetch (mirrors
        // useConfirmEvidence). The broad list refetch is likewise deferred.
        void qc.invalidateQueries({ queryKey: key, refetchType: 'none' });
        void qc.invalidateQueries({
          queryKey: certifiedMaterialsKeys.all,
          refetchType: 'none',
        });
      } catch (err) {
        if (prev) qc.setQueryData(key, prev);
        throw err;
      }
    };

  const validate = useMutation({ mutationFn: run(validateFieldMatch, true) });
  const revoke = useMutation({
    mutationFn: run(revokeFieldMatchValidation, false),
  });
  return { validate, revoke };
}
